import { faEllipsis } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Application } from '@pickleballinc/lib/types';
import { useCollapseNav } from '@pickleballinc/react-ui';
import clsx from 'clsx';
import { usePathname } from 'next/navigation';
import { forwardRef, HTMLProps, ReactNode, RefObject, useEffect, useRef, useState } from 'react';

import CustomLink from '../../ui/custom-link';
import { BLOG_LABEL } from '../utils/constants';
import { NavItem } from '../utils/nav.config';

import { DropdownPortal } from './DropdownPortal';

function isActiveMenuItem(
	href: string,
	pathname: string,
	baseOrigin: string,
	pickleballHref: string,
	wprHref: string,
	clubsHref: string,
	teamLeaguesHref: string,
	leaguesHref: string,
	tournamentHref: string,
	application: Application,
	suffix?: string
): boolean {
	if (baseOrigin) {
		const fullURL = `${baseOrigin}${pathname}`;

		if (baseOrigin.includes(pickleballHref)) {
			const rewrittenHref = href === `https://${pickleballHref}/news/all` ? `https://${pickleballHref}/news` : href;

			return fullURL.startsWith(rewrittenHref);
		} else if ([clubsHref, teamLeaguesHref, leaguesHref, tournamentHref].includes(baseOrigin)) {
			if (application === 'leagues' && pathname === '/leagues/pricing') {
				if (suffix === '/leagues/pricing') {
					return pathname.startsWith(suffix);
				} else return false;
			} else if (pathname === '/pricing') {
				if (suffix === '/pricing') {
					return pathname.startsWith(suffix);
				} else return false;
			}
			return fullURL.startsWith(href);
		} else if (wprHref.includes(baseOrigin)) {
			return suffix ? pathname === suffix : pathname === '/';
		}
	}

	if (application === 'clubs') {
		if (pathname === '/pricing') {
			if (suffix === '/pricing') {
				return pathname.startsWith(suffix);
			} else return false;
		} else {
			if (suffix === '/clubs') {
				return true;
			} else {
				return false;
			}
		}
	}

	if (application === 'tournament') {
		if (pathname === '/pricing') {
			if (suffix === '/pricing') {
				return pathname.startsWith(suffix);
			} else return false;
		} else {
			if (suffix === '/tournaments') {
				return true;
			} else {
				return false;
			}
		}
	}

	if (application === 'leagues') {
		if (pathname === '/leagues/pricing') {
			if (suffix === '/leagues/pricing') {
				return pathname.startsWith(suffix);
			} else return false;
		} else {
			if (suffix === '/leagues') {
				return true;
			} else {
				return false;
			}
		}
	}

	if (application === 'team-leagues') {
		if (pathname === '/pricing') {
			if (suffix === '/pricing') {
				return pathname.startsWith(suffix);
			} else return false;
		} else {
			if (suffix === '/team-leagues') {
				return true;
			} else {
				return false;
			}
		}
	}

	if (application === 'apr-rankings') {
		return pathname === href;
	}

	return suffix ? pathname.startsWith(suffix) : false;
}

interface MenuItemProps {
	children: ReactNode;
	href: string;
	baseOrigin: string;
	pickleballHref: string;
	aprHref: string;
	clubsHref: string;
	teamLeaguesHref: string;
	leaguesHref: string;
	tournamentsHref: string;
	application: Application;
	suffix?: string;
}

const MenuItem = forwardRef<HTMLDivElement, HTMLProps<HTMLDivElement> & MenuItemProps>(function MenuItem(props, propsRef) {
	const {
		className,
		href,
		children,
		baseOrigin,
		pickleballHref,
		aprHref,
		leaguesHref,
		teamLeaguesHref,
		clubsHref,
		tournamentsHref,
		suffix,
		application,
		...rest
	} = props;
	const pathname = usePathname();

	const menuItemClasses = clsx(
		'relative flex font-semibold text-white transition-all duration-150 hover:text-opacity-60',
		{
			'before:content-[""] before:absolute before:inset-x-0 before:bottom-1 before:h-1 before:bg-white': isActiveMenuItem(
				href,
				pathname || '',
				baseOrigin,
				pickleballHref,
				aprHref,
				clubsHref,
				teamLeaguesHref,
				leaguesHref,
				tournamentsHref,
				application,
				suffix
			)
		},
		className
	);

	return (
		<div className={menuItemClasses} {...rest} ref={propsRef}>
			<CustomLink className="flex items-center px-2" href={href} target={props.target}>
				{children}
			</CustomLink>
		</div>
	);
});

interface NavProps {
	baseOrigin: string;
	application: Application;
	pickleballHref: string;
	aprHref: string;
	tournamentsHref: string;
	clubsHref: string;
	teamLeaguesHref: string;
	leaguesHref: string;
	navItems: NavItem[];
}

export const Nav = ({
	baseOrigin,
	application,
	pickleballHref,
	aprHref,
	tournamentsHref,
	clubsHref,
	teamLeaguesHref,
	leaguesHref,
	navItems
}: NavProps) => {
	const pathname = usePathname();
	const containerRef = useRef<HTMLDivElement>(null);
	const { hidden, registerItem, ready } = useCollapseNav({
		containerRef: containerRef as RefObject<HTMLElement>
	});
	const [isDropdownOpen, setIsDropdownOpen] = useState(false);
	const dropdownRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
				setIsDropdownOpen(false);
			}
		};

		if (isDropdownOpen) {
			document.addEventListener('click', handleClickOutside);
		} else {
			document.removeEventListener('click', handleClickOutside);
		}

		return () => document.removeEventListener('click', handleClickOutside);
	}, [isDropdownOpen]);

	return (
		<div
			className={`relative hidden flex-1 gap-1 overflow-hidden text-sm text-white transition-opacity duration-200 sm:inline-flex ${
				ready ? 'opacity-100' : 'opacity-0'
			}`}
			ref={containerRef}
		>
			<ul className="flex items-center gap-1 flex-nowrap whitespace-nowrap">
				{navItems
					.filter((item) => item.children !== BLOG_LABEL)
					.map((item, index) => (
						<li key={index} className="h-[56px]">
							<MenuItem
								className={`${hidden.some((id) => id === item.id) ? 'select-none opacity-0' : ''} h-full`}
								data-collid={item.id}
								target={item.target}
								rel="noopener noreferrer"
								ref={(el) => registerItem(el, item.id, index)}
								href={item.href}
								suffix={item.suffix}
								baseOrigin={baseOrigin}
								pickleballHref={pickleballHref}
								aprHref={aprHref}
								clubsHref={clubsHref}
								teamLeaguesHref={teamLeaguesHref}
								leaguesHref={leaguesHref}
								tournamentsHref={tournamentsHref}
								application={application}
								data-testid={`nav-${item.children}`}
							>
								{item.children}
							</MenuItem>
						</li>
					))}
			</ul>
			{hidden.length > 0 && (
				<div className="sticky inset-y-0 right-0 ml-auto inline-flex px-6" ref={dropdownRef}>
					<div className="flex cursor-pointer items-center" onClick={() => setIsDropdownOpen((prev) => !prev)}>
						<FontAwesomeIcon icon={faEllipsis} className="block text-xl" />
					</div>
					{isDropdownOpen ? (
						<DropdownPortal>
							<div
								className="absolute z-50 min-w-[160px] transform -translate-x-12 rounded-b-lg bg-white pt-2 pb-1.5 shadow-lg"
								style={{
									position: 'absolute',
									top: dropdownRef.current?.getBoundingClientRect().bottom ?? 0,
									left: dropdownRef.current?.getBoundingClientRect().left ?? 0
								}}
							>
								{navItems
									.filter((item) => hidden.some((id) => id === item.id))
									.map((item) => (
										<div key={item.id} className="cursor-pointer px-4 py-1 text-sm font-normal text-black hover:underline">
											<CustomLink
												href={item.href}
												target={item.target}
												className={`outline-0 ${pathname?.startsWith(item.href) ? 'underline' : ''}`}
											>
												{item.children}
											</CustomLink>
										</div>
									))}
							</div>
						</DropdownPortal>
					) : null}
				</div>
			)}
		</div>
	);
};
